// IDPF Font Deobfuscation
//
// Self-contained module to support EPUB font obfuscation per the IDPF 2008
// spec (http://www.idpf.org/2008/embedding) and Adobe RC obfuscation
// (http://ns.adobe.com/pdf/enc#RC).
//
// Flow:
//   1. Read META-INF/encryption.xml to discover obfuscated resources.
//   2. Read the OPF and extract the publication's unique-identifier.
//   3. Derive the obfuscation key (SHA-1 for IDPF, UUID bytes for Adobe).
//   4. Fetch each obfuscated font, XOR the prefix bytes back to plain.
//   5. Expose each deobfuscated font as a blob: URL and rewrite any
//      url(...) references in the cached CSS to point to those blob URLs
//      (the iframe browser fetches fonts via HTTP, bypassing JS fetchers,
//      so blob URL substitution is required).
//
// To remove this feature: delete this module and the
// setupIdpfFontDeobfuscation() call inside EpubViewer.load() (and the
// matching cleanup inside EpubViewer.destroy()).

import { Link } from '@readium/shared';
import { resolveRelativePath } from './cssInliner.js';

const IDPF_OBFUSCATION_ALGORITHM = 'http://www.idpf.org/2008/embedding';
const ADOBE_OBFUSCATION_ALGORITHM = 'http://ns.adobe.com/pdf/enc#RC';
const IDPF_OBFUSCATION_PREFIX_LEN = 1040; // 52 × 20-byte SHA-1
const ADOBE_OBFUSCATION_PREFIX_LEN = 1024; // 64 × 16-byte UID

/**
 * Parse META-INF/encryption.xml and return entries whose algorithm matches
 * a supported font obfuscation scheme. Hrefs are decoded from URI form and
 * are publication-relative (relative to the OCF root).
 *
 * @param {string} xmlText
 * @returns {Array<{href: string, algorithm: string}>}
 */
function parseEncryptionXmlForObfuscation(xmlText) {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, 'application/xml');
    if (xmlDoc.querySelector('parsererror')) return [];

    const supported = new Set([IDPF_OBFUSCATION_ALGORITHM, ADOBE_OBFUSCATION_ALGORITHM]);
    const results = [];
    const encryptedDataNodes = xmlDoc.getElementsByTagNameNS('*', 'EncryptedData');

    for (const node of encryptedDataNodes) {
        const method = node.getElementsByTagNameNS('*', 'EncryptionMethod')[0];
        const cipherRef = node.getElementsByTagNameNS('*', 'CipherReference')[0];
        if (!method || !cipherRef) continue;

        const algorithm = method.getAttribute('Algorithm');
        if (!supported.has(algorithm)) continue;

        const uri = cipherRef.getAttribute('URI');
        if (!uri) continue;

        let href;
        try {
            href = decodeURIComponent(uri);
        } catch {
            href = uri;
        }
        results.push({ href, algorithm });
    }
    return results;
}

/**
 * Extract the publication's unique-identifier value from the OPF document.
 * Resolves package@unique-identifier to its matching <dc:identifier> element.
 * Falls back to the first <dc:identifier> if the IDREF can't be resolved.
 *
 * @param {string} opfText
 * @returns {string|null}
 */
function extractOpfUniqueIdentifier(opfText) {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(opfText, 'application/xml');
    if (xmlDoc.querySelector('parsererror')) return null;

    const pkg = xmlDoc.getElementsByTagNameNS('*', 'package')[0];
    if (!pkg) return null;

    const idRef = pkg.getAttribute('unique-identifier');
    const identifiers = xmlDoc.getElementsByTagNameNS('*', 'identifier');

    let target = null;
    if (idRef) {
        for (const id of identifiers) {
            if (id.getAttribute('id') === idRef) {
                target = id;
                break;
            }
        }
    }
    if (!target && identifiers.length > 0) target = identifiers[0];
    return target ? target.textContent : null;
}

/**
 * Compute the IDPF obfuscation key: SHA-1 of the unique identifier with
 * whitespace stripped (U+0020, U+0009, U+000D, U+000A), as a 20-byte
 * Uint8Array.
 *
 * @param {string} identifier
 * @returns {Promise<Uint8Array>}
 */
async function computeIdpfFontKey(identifier) {
    const stripped = identifier.replace(/[\u0020\u0009\u000D\u000A]/g, '');
    const buf = new TextEncoder().encode(stripped);
    const digest = await crypto.subtle.digest('SHA-1', buf);
    return new Uint8Array(digest);
}

/**
 * Compute the Adobe obfuscation key: parse the unique identifier as a UUID
 * and return its 16 raw bytes. Returns null if the identifier isn't a
 * recognizable UUID hex string.
 *
 * @param {string} identifier
 * @returns {Uint8Array|null}
 */
function computeAdobeFontKey(identifier) {
    const hex = identifier.replace(/^urn:uuid:/i, '').replace(/-/g, '');
    if (!/^[0-9a-fA-F]{32}$/.test(hex)) return null;
    const key = new Uint8Array(16);
    for (let i = 0; i < 16; i++) {
        key[i] = parseInt(hex.substr(i * 2, 2), 16);
    }
    return key;
}

/**
 * XOR the first prefixLength bytes of the input with a cycled key.
 * Returns a new Uint8Array containing the deobfuscated content; the
 * tail (beyond prefixLength) is copied unchanged.
 *
 * @param {Uint8Array} input
 * @param {Uint8Array} key
 * @param {number} prefixLength
 * @returns {Uint8Array}
 */
function deobfuscateFontBytes(input, key, prefixLength) {
    const output = new Uint8Array(input.length);
    output.set(input);

    const limit = Math.min(prefixLength, output.length);
    for (let i = 0; i < limit; i++) {
        output[i] = input[i] ^ key[i % key.length];
    }
    return output;
}

/**
 * Build a lookup map keyed by lowercased filename so url() rewrites can
 * recover from path mismatches (different folder roots, absolute paths,
 * mixed-case extensions like .TTF vs .ttf, etc.).
 *
 * @param {Map<string, string>} fontBlobUrls
 * @returns {Map<string, string>}
 */
function buildFontFilenameIndex(fontBlobUrls) {
    const index = new Map();
    for (const [href, blobUrl] of fontBlobUrls) {
        const filename = href.split('/').pop().toLowerCase();
        if (filename) index.set(filename, blobUrl);
    }
    return index;
}

/**
 * Resolve a url() target against the deobfuscated font map. Tries:
 *   1. exact publication-relative path match (after relative resolution)
 *   2. raw stripped-leading-slash match (handles absolute paths)
 *   3. filename-only match (case-insensitive) — needed because authors
 *      mix relative/absolute/OPF-relative paths and case.
 *
 * @returns {string|null}
 */
function lookupFontBlobUrl(cssDir, rawUrl, fontBlobUrls, filenameIndex) {
    const cleanUrl = rawUrl.split('?')[0].split('#')[0];
    const stripped = cleanUrl.replace(/^\//, '');

    const absPath = resolveRelativePath(cssDir, stripped);
    if (fontBlobUrls.has(absPath)) return fontBlobUrls.get(absPath);
    if (fontBlobUrls.has(stripped)) return fontBlobUrls.get(stripped);

    const filename = cleanUrl.split('/').pop().toLowerCase();
    return filenameIndex.get(filename) ?? null;
}

/**
 * Replace url(...) font references in arbitrary text (HTML or CSS) with
 * blob URLs from the deobfuscated font map. Used by DecryptingResource
 * to cover @font-face declarations that live in inline <style> blocks
 * inside XHTML documents (which never enter the CSS cache).
 *
 * @param {string} text
 * @param {Map<string, string>} fontBlobUrls
 * @param {string} baseDir — publication-relative directory of the document
 * @returns {string}
 */
export function rewriteFontUrlsInText(text, fontBlobUrls, baseDir) {
    if (!fontBlobUrls || fontBlobUrls.size === 0) return text;
    const filenameIndex = buildFontFilenameIndex(fontBlobUrls);
    return text.replace(/url\(\s*(['"]?)([^'")]+)\1\s*\)/gi, (match, quote, rawUrl) => {
        if (
            rawUrl.startsWith('data:') ||
            rawUrl.startsWith('blob:') ||
            rawUrl.startsWith('http://') ||
            rawUrl.startsWith('https://') ||
            rawUrl.startsWith('//')
        ) {
            return match;
        }
        const blobUrl = lookupFontBlobUrl(baseDir, rawUrl, fontBlobUrls, filenameIndex);
        if (!blobUrl) return match;
        return `url(${quote}${blobUrl}${quote})`;
    });
}

/**
 * Best-effort font MIME type lookup based on file extension.
 * Used so the resulting blob URLs carry an appropriate Content-Type.
 *
 * @param {string} href
 * @returns {string}
 */
function guessFontMimeType(href) {
    const lower = href.toLowerCase();
    if (lower.endsWith('.otf')) return 'font/otf';
    if (lower.endsWith('.ttf')) return 'font/ttf';
    if (lower.endsWith('.woff')) return 'font/woff';
    if (lower.endsWith('.woff2')) return 'font/woff2';
    console.warn('[IDPF font deobfuscation] Unrecognized font extension, falling back to octet-stream:', href);
    return 'application/octet-stream';
}

/**
 * Read a resource as a string through the publication fetcher. Goes through
 * the full DecryptingResource pipeline so that publica.la-encrypted OCF
 * metadata (encryption.xml, container.xml — matched by the .xml filter in
 * ContentEncryptionService.shouldDecryptFile) is transparently decrypted.
 *
 * @param {Object} fetcher
 * @param {string} href
 * @returns {Promise<string>}
 */
function readResourceAsString(fetcher, href) {
    return fetcher.get(new Link({ href })).readAsString();
}

/**
 * Extract the OPF path from container.xml. Standalone helper so the IDPF
 * section doesn't depend on EpubViewer.getOpfPathFromContainer (which uses
 * the decrypting fetcher and would also need the bypass).
 *
 * @param {string} xmlText
 * @returns {string|null}
 */
function extractOpfPathFromContainerXml(xmlText) {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, 'application/xml');
    if (xmlDoc.querySelector('parsererror')) return null;

    const rootfiles = xmlDoc.getElementsByTagNameNS('*', 'rootfile');
    for (const rootfile of rootfiles) {
        if (rootfile.getAttribute('media-type') === 'application/oebps-package+xml') {
            return rootfile.getAttribute('full-path');
        }
    }
    return null;
}

/**
 * Fallback path: read container.xml and the OPF to obtain the unique
 * identifier when the webpub-manifest doesn't expose `metadata.identifier`.
 * Returns null on any failure — callers should treat that as "no identifier
 * available" and abort deobfuscation.
 *
 * @param {Object} fetcher
 * @returns {Promise<string|null>}
 */
async function fetchOpfUniqueIdentifier(fetcher) {
    let containerXmlText;
    try {
        containerXmlText = await readResourceAsString(fetcher, 'META-INF/container.xml');
    } catch (error) {
        console.warn('[IDPF font deobfuscation] Could not fetch container.xml:', error);
        return null;
    }

    const opfPath = extractOpfPathFromContainerXml(containerXmlText);
    if (!opfPath) {
        console.warn('[IDPF font deobfuscation] No OPF path in container.xml');
        return null;
    }

    let opfText;
    try {
        opfText = await readResourceAsString(fetcher, opfPath);
    } catch (error) {
        console.warn('[IDPF font deobfuscation] Could not fetch OPF:', error);
        return null;
    }

    return extractOpfUniqueIdentifier(opfText);
}

/**
 * Top-level orchestrator: reads encryption.xml + container.xml + OPF,
 * deobfuscates each listed font into a blob URL.
 *
 * Independent of CSS fetching: returns a font href → blob URL map.
 * The lazy CSS cache reads this map when processing each CSS, so the
 * mapping is consumed at decrypt time rather than via a bulk rewrite.
 *
 * @param {Object} fetcher — the publication fetcher
 * @param {string|null} manifestIdentifier — publication unique-identifier as
 *        already exposed in the readium webpub-manifest (`metadata.identifier`).
 *        When present, lets us skip fetching `META-INF/container.xml` and the
 *        OPF entirely (saves 2 round-trips on every book open with obfuscated
 *        fonts). Streamers like go-toolkit and r2-streamer-js populate this
 *        field by extracting the OPF's `<dc:identifier>` referenced by
 *        `package@unique-identifier`, so the value is byte-equivalent to what
 *        we would read from the OPF directly. Null/missing falls back to the
 *        legacy container.xml + OPF path.
 * @returns {Promise<Map<string, string>>} href → blob URL (revoke on cleanup)
 */
export async function setupIdpfFontDeobfuscation(fetcher, manifestIdentifier = null) {
    const blobUrls = new Map();

    let encryptionXmlText;
    try {
        encryptionXmlText = await readResourceAsString(fetcher, 'META-INF/encryption.xml');
    } catch {
        // No encryption.xml — nothing obfuscated.
        return blobUrls;
    }

    const entries = parseEncryptionXmlForObfuscation(encryptionXmlText);
    if (entries.length === 0) return blobUrls;

    // Prefer the identifier already exposed by the webpub-manifest. Falls
    // back to fetching container.xml + OPF only when the manifest doesn't
    // expose one (custom or legacy streamers).
    let identifier = manifestIdentifier;
    if (!identifier) {
        identifier = await fetchOpfUniqueIdentifier(fetcher);
    }
    if (!identifier) {
        console.warn('[IDPF font deobfuscation] No unique identifier available (manifest or OPF)');
        return blobUrls;
    }

    const idpfKey = await computeIdpfFontKey(identifier);
    const adobeKey = computeAdobeFontKey(identifier);

    // Deobfuscate fonts in parallel — each font is an independent fetch+XOR.
    await Promise.all(
        entries.map(async ({ href, algorithm }) => {
            let key, prefixLen;
            if (algorithm === IDPF_OBFUSCATION_ALGORITHM) {
                key = idpfKey;
                prefixLen = IDPF_OBFUSCATION_PREFIX_LEN;
            } else if (algorithm === ADOBE_OBFUSCATION_ALGORITHM && adobeKey) {
                key = adobeKey;
                prefixLen = ADOBE_OBFUSCATION_PREFIX_LEN;
            } else {
                return;
            }

            try {
                const fontRes = fetcher.get(new Link({ href }));
                const raw = await fontRes.read();
                if (!raw) {
                    console.warn('[IDPF font deobfuscation] Empty response for', href);
                    return;
                }
                // Defensive: read() is contractually Uint8Array | undefined, but a
                // future readium version or a custom fetcher wrapper could return
                // a bare ArrayBuffer. output.set() would still work, but element
                // access (input[i]) would yield undefined → NaN → 0, silently
                // leaving the font obfuscated. Normalize to Uint8Array.
                const obfuscatedBytes = raw instanceof Uint8Array ? raw : new Uint8Array(raw);
                const deobfuscated = deobfuscateFontBytes(obfuscatedBytes, key, prefixLen);
                const blobUrl = URL.createObjectURL(new Blob([deobfuscated], { type: guessFontMimeType(href) }));
                blobUrls.set(href, blobUrl);
            } catch (error) {
                console.warn('[IDPF font deobfuscation] Failed for', href, error);
            }
        })
    );

    return blobUrls;
}
